import { useEffect, useRef, useState } from "react";
import { Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";

export const LikeButton = ({ type, itemId, count = 0, label = "Love this", size = "sm", className = "" }) => {
  const [likes, setLikes] = useState(count);
  const [popped, setPopped] = useState(false);
  const [bursts, setBursts] = useState([]);
  const touched = useRef(false);

  // Sync from server-provided count until the mama interacts.
  useEffect(() => {
    if (!touched.current) setLikes(count);
  }, [count]);

  const handleLike = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    touched.current = true;
    setLikes((n) => n + 1);
    setPopped(true);
    setTimeout(() => setPopped(false), 260);
    const id = Date.now() + Math.random();
    setBursts((b) => [...b, id]);
    setTimeout(() => setBursts((b) => b.filter((x) => x !== id)), 800);
    try {
      const r = await api.post(`/likes/${type}/${encodeURIComponent(itemId)}`);
      if (typeof r.data?.count === "number") setLikes(r.data.count);
    } catch {
      /* keep the optimistic bump */
    }
  };

  const isLg = size === "lg";

  return (
    <button
      type="button"
      onClick={handleLike}
      data-testid={`like-button-${type}-${itemId}`}
      aria-label={`${label} (${likes} likes)`}
      className={`relative inline-flex items-center gap-2 rounded-full font-semibold transition-colors select-none ${
        isLg
          ? "bg-primary text-primary-foreground px-7 py-3 text-base hover:-translate-y-0.5 soft-shadow"
          : "border border-border bg-card px-3.5 py-1.5 text-sm text-foreground hover:bg-accent"
      } ${className}`}
    >
      <span className="relative inline-flex">
        <motion.span animate={popped ? { scale: [1, 1.4, 1] } : { scale: 1 }} transition={{ duration: 0.26 }}>
          <Heart
            className={isLg ? "h-5 w-5" : "h-4 w-4"}
            fill="currentColor"
            style={{ color: isLg ? undefined : "hsl(var(--primary))" }}
          />
        </motion.span>
        <AnimatePresence>
          {bursts.map((id) => (
            <motion.span
              key={id}
              initial={{ opacity: 1, y: 0, scale: 0.8 }}
              animate={{ opacity: 0, y: -22, scale: 1.1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="pointer-events-none absolute -top-1 left-1/2 -translate-x-1/2"
            >
              <Heart className="h-3.5 w-3.5" fill="currentColor" style={{ color: "hsl(var(--primary))" }} />
            </motion.span>
          ))}
        </AnimatePresence>
      </span>
      {isLg && <span>{label}</span>}
      <span data-testid={`like-count-${type}-${itemId}`} className="tabular-nums">
        {likes}
      </span>
    </button>
  );
};

export default LikeButton;
